import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { adminApi } from '../lib/api';
import { Building2, Users, CreditCard, Tag, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminDashboard() {
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        adminApi.getPlatformSettings().then(({ data }) => setSettings(data)).catch(() => toast.error('Failed to load platform settings')).finally(() => setLoading(false));
    }, []);

    const stats = [
        { label: 'Registered Businesses', value: settings?.total_businesses || 0, icon: Building2, color: 'text-blue-400 bg-blue-500/10' },
        { label: 'Total Users', value: settings?.total_users || 0, icon: Users, color: 'text-emerald-400 bg-emerald-500/10' },
        { label: 'Active Subscriptions', value: settings?.active_subscriptions || 0, icon: CreditCard, color: 'brand-icon brand-bg-subtle' },
        { label: 'Monthly Price', value: `£${settings?.default_monthly_price?.toFixed(2) || '29.99'}`, icon: TrendingUp, color: 'text-purple-400 bg-purple-500/10' },
    ];

    return (
        <Layout>
            <div data-testid="admin-dashboard">
                <div className="mb-6">
                    <h1 className="text-2xl font-bold text-slate-100">Platform Overview</h1>
                    <p className="text-slate-400 text-sm mt-1">Manage the Food Safe Hub platform</p>
                </div>

                {loading ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {[1, 2, 3, 4].map(i => <div key={i} className="h-28 bg-slate-900/60 rounded-xl animate-pulse" />)}
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {stats.map((s) => (
                            <div key={s.label} className="bg-slate-900/60 border border-slate-800 rounded-xl p-5" data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, '-')}`}>
                                <div className="flex items-center justify-between mb-3">
                                    <span className="text-sm text-slate-400">{s.label}</span>
                                    <div className={`p-2 rounded-lg ${s.color}`}><s.icon className="h-4 w-4" /></div>
                                </div>
                                <p className="text-2xl font-bold text-slate-100">{s.value}</p>
                            </div>
                        ))}
                    </div>
                )}

                <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-5">
                        <h2 className="text-lg font-semibold text-slate-200 mb-3 flex items-center gap-2">
                            <Building2 className="h-5 w-5 text-blue-400" /> Quick Actions
                        </h2>
                        <div className="space-y-2">
                            <a href="/admin/businesses" className="block p-3 bg-slate-800/50 rounded-lg hover:bg-slate-800 transition-colors text-slate-300 text-sm">View all businesses</a>
                            <a href="/admin/coupons" className="block p-3 bg-slate-800/50 rounded-lg hover:bg-slate-800 transition-colors text-slate-300 text-sm">Manage coupons</a>
                        </div>
                    </div>
                    <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-5">
                        <h2 className="text-lg font-semibold text-slate-200 mb-3 flex items-center gap-2">
                            <Tag className="h-5 w-5 brand-icon" /> Platform Info
                        </h2>
                        <div className="space-y-2 text-sm text-slate-400">
                            <p>Default subscription: <span className="brand-text font-medium">£{settings?.default_monthly_price?.toFixed(2) || '29.99'}/month</span></p>
                            <p>Switch to a business context to view their SFBB data using the Businesses page.</p>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
}
